<script>
  import { _, locale, lget } from "./i18n.js";
  import { tick, onMount, onDestroy } from "svelte";
  import {
    f7,
    Page,
    Navbar,
    NavLeft,
    NavRight,
    BlockTitle,
    List,
    ListItem,
    ListInput,
    AccordionContent,
    Card,
    CardContent,
    CardFooter,
    Searchbar,
    Button,
    Toolbar,
    Link,
    Icon
  } from "framework7-svelte";
  import AddressCanvas from "./AddressCanvas.svelte";
  import { getProfile, resetProfile } from "./UserService.js";
  import {
    getSecuredFirestore,
    firebase,
    getCachedFirst
  } from "./Firestore.js";
  import {
    formatAddress,
    deleteAddress,
    deletableAddress,
    setPrimaryAddress,
    getStaticDeliveryLocationImage,
    sendDeliveryRequest,
    getDeliverableIcon,
    getDeliverableColor,
    NEW_LINE,
    HTML,
    COMPACT
  } from "./AddressService.js";
  import { isValidForm } from "./Validation.js";
  import {
    createBuilding,
    createMoo,
    createStreet,
    createSubdistrict,
    createDistrict,
    createProvince,
    createZipcode,
    createCountry,
    createDeliverable,
    createDeliveryLocation,
    dirty,
    map
  } from "./stores.js";
  import {
    initAutoComplete,
    getPlaceDetails,
    getAddressFromPlaceDetails,
    isDeliverable,
    geocode
  } from "./MapService.js";
  import { getLanguageFromLocale } from "./Utils.js";
  import haversine from "haversine";

  dirty.set(false);

  export let f7route;
  export let f7router;
  f7router.allowPageChange = false;
  let addressDoc;
  let building = null;
  let moo = null;
  let street = null;
  let subdistrict = null;
  let district = null;
  let districtRef;
  let province = null;
  let provinceRef;
  let zipcode = null;
  let deliverable = null;
  let country = null;
  let location = null;
  let deliveryLocation;
  let deliveryLocationUnsubscribe;
  let primary;
  $: primaryClass = primary ? "disabled" : "";
  let valid = null;
  let mismatch = false;

  $: locationImage = $deliveryLocation
    ? getStaticDeliveryLocationImage($deliveryLocation)
    : null;

  let userId = f7route.params.userId;
  let addressId = f7route.params.addressId;
  addressId = addressId === "New" ? null : addressId;

  $: navTitle = addressId
    ? $_("address.navTitle.edit")
    : $_("address.navTitle.add");
  $: mapService = $map ? new google.maps.places.PlacesService($map) : null;

  async function getCurrentUserId() {
    return userId ? userId : getProfile().then(profile => profile.userId);
  }

  if (addressId) {
    f7.preloader.show();

    getCurrentUserId().then(function(userId) {
      getSecuredFirestore().then(function(firestore) {
        addressDoc = firestore
          .collection("users/" + userId + "/addresses")
          .doc(addressId);
        getCachedFirst(addressDoc).then(function(address) {
          building = createBuilding(address.get("building"));
          moo = createMoo(address.get("moo"));
          street = createStreet(address.get("street"));
          subdistrict = createSubdistrict(address.get("subdistrict"));
          district = createDistrict(address.get("district"));
          province = createProvince(address.get("province"));
          zipcode = createZipcode(address.get("zipcode"));
          country = createCountry(address.get("country"));
          let geoPoint = address.get("location");
          location = geoPoint
            ? { lat: geoPoint.latitude, lng: geoPoint.longitude }
            : null;

          deliverable = createDeliverable(address.get("deliverable"));
          deliveryLocation = createDeliveryLocation(location);
          deliveryLocationUnsubscribe = deliveryLocation.subscribe(value => {
            updateLocation(value, value != null && value.checkLocation);
          });
          mismatch = address.get("mismatch");
          primary = address.get("primary");

          f7.preloader.hide();
        });
      });
    });
  } else {
    building = createBuilding("");
    moo = createMoo("");
    street = createStreet("");
    subdistrict = createSubdistrict("");
    district = createDistrict("");
    province = createProvince($_("address.defaultProvince"));
    zipcode = createZipcode("");
    country = createCountry($_("address.defaultCountry"));
    deliverable = createDeliverable(false);
    deliveryLocation = createDeliveryLocation(location);
    deliveryLocationUnsubscribe = deliveryLocation.subscribe(value => {
      updateLocation(value, value != null && value.checkLocation);
    });

    f7.dialog
      .create({
        title: $_("address.dialog.new.title"),
        text: $_("address.dialog.new.description"),
        buttons: [
          {
            text: $_("address.dialog.button.searchByName"),
            bold: true,
            onClick: function() {
              Dom7(".page-current .searchbar-enable").trigger("click");
            }
          },
          {
            text: $_("address.dialog.button.findOnMap"),
            onClick: function() {
              f7router.navigate("/Location", {
                props: {
                  building: building,
                  moo: moo,
                  street: street,
                  subdistrict: subdistrict,
                  district: district,
                  province: province,
                  zipcode: zipcode,
                  country: country,
                  deliverable: deliverable,
                  deliveryLocation: deliveryLocation,
                  addressId: addressId
                }
              });
            }
          },
          {
            text: $_("address.dialog.button.enterManually")
          }
        ],
        verticalButtons: true
      })
      .open();
  }

  $: address = formatAddress({
    building: $building,
    moo: $moo,
    street: $street,
    subdistrict: $subdistrict,
    district: $district,
    province: $province,
    country: $country,
    zipcode: $zipcode,
    mode: NEW_LINE
  });

  function updateBuilding(value, checkLocation) {
    if (value != $building) {
      building.set(value);
      dirty.set(true);
      validate();
      if (checkLocation) {
        validateLocation();
      }
    }
  }

  function updateMoo(value, checkLocation) {
    if (value != $moo) {
      moo.set(value);
      dirty.set(true);
      validate();
      if (checkLocation) {
        validateLocation();
      }
    }
  }

  function updateStreet(value, checkLocation) {
    if (value != $street) {
      street.set(value);
      dirty.set(true);
      validate();
      if (checkLocation) {
        validateLocation();
      }
    }
  }

  function updateSubdistrict(value, checkLocation) {
    if (value != $subdistrict) {
      subdistrict.set(value);
      dirty.set(true);
      validate();
      if (checkLocation) {
        validateLocation();
      }
    }
  }

  function updateDistrict(value, checkLocation) {
    if (value === "") {
      districtRef = null;
    }

    if (value != $district) {
      district.set(value);
      dirty.set(true);
      validate();
      if (checkLocation) {
        validateLocation();
      }
    }
  }

  function updateProvince(value, checkLocation) {
    if (value === "") {
      provinceRef = null;
    }

    if (value != $province) {
      province.set(value);
      dirty.set(true);
      validate();
      if (checkLocation) {
        validateLocation();
      }
    }
  }

  function updateZipcode(value, checkLocation) {
    if (value != $zipcode) {
      zipcode.set(value);
      dirty.set(true);
      validate();
      if (checkLocation) {
        validateLocation();
      }
    }
  }

  function updateLocation(value, checkLocation) {
    if (JSON.stringify(value) !== JSON.stringify(location)) {
      location = value;
      dirty.set(true);
      validate();
      if (checkLocation) {
        validateLocation();
      }
    }
  }

  function validate() {
    valid = isValidForm();
  }

  function validateLocation() {
    if (valid && location != null) {
      let address = {
        building: $building,
        moo: $moo,
        street: $street,
        subdistrict: $subdistrict,
        district: $district,
        province: $province,
        country: $country,
        zipcode: $zipcode,
        mode: COMPACT
      };
      geocode(formatAddress(address)).then(function(locations) {
        mismatch = !locations.some(
          item =>
            haversine(
              { latitude: location.lat, longitude: location.lng },
              { latitude: item.lat, longitude: item.lng },
              { unit: "meter" }
            ) < 3000
        );
      });
    }
  }

  let beforeunloadListener = function(e) {
    if ($dirty) {
      e.preventDefault();
    }
  };

  function showUnsavedChangesDialog(callback) {
    f7.dialog
      .create({
        title: $_("address.dialog.unsavedChanges.title"),
        text: $_("address.dialog.unsavedChanges.description"),
        buttons: [
          {
            bold: true,
            text: $_("address.dialog.button.saveChanges"),
            onClick: function() {
              if (addressDoc) {
                save()
                  .then(function() {
                    if (callback) {
                      callback();
                    }
                  })
                  .catch(function(e) {
                    console.log(e);
                    f7.dialog.alert(
                      $_("address.dialog.save.error"),
                      $_("address.dialog.button.saveChanges")
                    );
                  });
              } else {
                create()
                  .then(function() {
                    if (callback) {
                      callback();
                    }
                  })
                  .catch(function(e) {
                    console.log(e);
                    f7.dialog.alert(
                      $_("address.dialog.save.error"),
                      $_("address.dialog.button.saveNewAddress")
                    );
                  });
              }
            }
          },
          {
            text: $_("address.dialog.button.discardChanges"),
            onClick: function() {
              dirty.set(false);
              if (callback) {
                callback();
              }
            }
          },
          {
            text: $_("base.cancel")
          }
        ],
        verticalButtons: true
      })
      .open();
  }

  onMount(async () => {
    window.addEventListener("beforeunload", beforeunloadListener);
  });

  function onPageInit() {
    let searchbarAutocomplete;
    let searchbar = f7.searchbar.create({
      el: ".page-current #searchbar-autocomplete",
      customSearch: true,
      closeOnSelect: true,
      on: {
        enable: function(searchbar) {
          setTimeout(() => {
            if (app.theme === "ios") {
              searchbar.$el.css("overflow", "visible");
            } else {
              searchbar.$el.parents(".navbar-inner").css("overflow", "visible");
            }

            if (searchbarAutocomplete) {
              searchbarAutocomplete.open();
            } else {
              initAutoComplete({}, function(value) {
                isDeliverable(value.location).then(function(result) {
                  deliverable.set(result);
                });
                deliveryLocation.set(value.location);
                searchbar.disable();

                getPlaceDetails(value.id).then(async function(place) {
                  let address = getAddressFromPlaceDetails(place);
                  updateBuilding(address.building, false);
                  updateMoo(address.moo, false);
                  updateStreet(address.street, false);
                  updateSubdistrict(address.subdistrict, false);
                  updateDistrict(address.district, false);
                  updateProvince(address.province, false);
                  updateZipcode(address.zipcode, false);
                  validateLocation();

                  await tick();
                  validate();
                });
              }).then(function(result) {
                searchbarAutocomplete = result;
                searchbarAutocomplete.open();
              });
            }
          }, 400);
        },
        disable: function(searchbar) {
          if (app.theme === "ios") {
            searchbar.$el.css("overflow", "");
          } else {
            searchbar.$el.parents(".navbar-inner").css("overflow", "");
          }
        }
      }
    });

    f7.autocomplete.create({
      inputEl: "#province",
      openIn: "dropdown",
      valueProperty: "name",
      textProperty: "name",
      typeahead: true,
      source: function(query, render) {
        getProvinces(query).then(function(provinces) {
          render(provinces);
        });
      },
      renderItem: function(item, index) {
        let province = this.items[index];
        let deliverable = province.doc.get("deliverable");
        let icon = getDeliverableIcon(deliverable);
        let color = getDeliverableColor(deliverable);
        return (
          "<li>" +
          '<label class="item-radio item-content" data-value="' +
          item.value +
          '">' +
          '<div class="item-inner">' +
          '<div class="item-title">' +
          '<span style="margin-right: 8px; color: ' +
          color +
          '" class="' +
          icon +
          '"></span>' +
          item.text +
          "</div>" +
          "</div>" +
          " </label>" +
          "</li>"
        );
      },
      on: {
        change: function(value) {
          if (value.length > 0 && value[0]) {
            provinceRef = value[0].doc.ref;
            updateProvince(value[0].name);
          } else {
            provinceRef = null;
          }
        }
      }
    });

    f7.autocomplete.create({
      inputEl: "#district",
      openIn: "dropdown",
      valueProperty: "name",
      textProperty: "name",
      typeahead: true,
      source: function(query, render) {
        getDistricts(query, provinceRef).then(function(districts) {
          render(districts);
        });
      },
      renderItem: function(item, index) {
        let district = this.items[index];
        let deliverable = district.doc.get("deliverable");
        let icon = getDeliverableIcon(deliverable);
        let color = getDeliverableColor(deliverable);
        return (
          "<li>" +
          '<label class="item-radio item-content" data-value="' +
          item.value +
          '">' +
          '<div class="item-inner">' +
          '<div class="item-title">' +
          '<span style="margin-right: 8px; color: ' +
          color +
          '" class="' +
          icon +
          '"></span>' +
          item.text +
          "</div>" +
          "</div>" +
          " </label>" +
          "</li>"
        );
      },
      on: {
        change: function(value) {
          if (value.length > 0 && value[0]) {
            districtRef = value[0].doc.ref;
            updateDistrict(value[0].name);
            value[0].doc
              .get("provinceRef")
              .get()
              .then(function(provinceDoc) {
                provinceRef = provinceDoc.ref;
                updateProvince(lget(provinceDoc, "name", $locale));
              });
          } else {
            districtRef = null;
          }
        }
      }
    });

    f7.autocomplete.create({
      inputEl: "#subdistrict",
      openIn: "dropdown",
      valueProperty: "name",
      textProperty: "name",
      typeahead: true,
      source: function(query, render) {
        getSubdistricts(query, districtRef).then(function(subdistricts) {
          render(subdistricts);
        });
      },
      renderItem: function(item, index) {
        let subdistrict = this.items[index];
        let deliverable = subdistrict.doc.get("deliverable");
        let icon = getDeliverableIcon(deliverable);
        let color = getDeliverableColor(deliverable);
        return (
          "<li>" +
          '<label class="item-radio item-content" data-value="' +
          item.value +
          '">' +
          '<div class="item-inner">' +
          '<div class="item-title">' +
          '<span style="margin-right: 8px; color: ' +
          color +
          '" class="' +
          icon +
          '"></span>' +
          item.text +
          "</div>" +
          "</div>" +
          " </label>" +
          "</li>"
        );
      },
      on: {
        change: function(value) {
          if (value.length > 0 && value[0]) {
            let doc = value[0].doc;
            updateSubdistrict(value[0].name);
            deliverable.set(doc.get("deliverable"));
            zipcode.set(doc.get("zipcode"));
            doc
              .get("districtRef")
              .get()
              .then(function(districtDoc) {
                districtRef = districtDoc.ref;
                updateDistrict(lget(districtDoc, "name", $locale));
                districtDoc
                  .get("provinceRef")
                  .get()
                  .then(function(provinceDoc) {
                    provinceRef = provinceDoc.ref;
                    updateProvince(lget(provinceDoc, "name", $locale));
                  });
              });
          }
        }
      }
    });
  }

  onDestroy(async () => {
    deliveryLocationUnsubscribe && deliveryLocationUnsubscribe();
    window.removeEventListener("beforeunload", beforeunloadListener);
  });

  function getProvinces(query) {
    let language = getLanguageFromLocale($locale);
    let sortField = "name";
    if (language === "th") {
      sortField = "name_th";
    }

    return getFirestore().then(function(firestore) {
      return firestore
        .collection("provinces")
        .orderBy(sortField)
        .get()
        .then(function(snapshot) {
          let results = [];
          snapshot.forEach(function(doc) {
            let name = lget(doc, "name", $locale);
            if (name.toLowerCase().indexOf(query.toLowerCase()) >= 0) {
              results.push({ doc: doc, name: name });
            }
          });
          return results;
        });
    });
  }

  function getDistricts(query, province) {
    return getFirestore().then(function(firestore) {
      let districts = firestore.collection("districts");
      if (province) {
        let provinceDoc = firestore.doc("provinces/" + province.id);
        districts = districts.where("provinceRef", "==", provinceDoc);
      }

      let language = getLanguageFromLocale($locale);
      let sortField = "name";
      if (language === "th") {
        sortField = "name_th";
      }

      return districts
        .orderBy(sortField)
        .get()
        .then(function(snapshot) {
          let results = [];
          snapshot.forEach(function(doc) {
            let name = lget(doc, "name", $locale);
            if (name.toLowerCase().indexOf(query.toLowerCase()) >= 0) {
              results.push({ doc: doc, name: name });
            }
          });
          return results;
        });
    });
  }

  function getSubdistricts(query, district) {
    return getFirestore().then(function(firestore) {
      let subdistricts = firestore.collection("subdistricts");
      if (district) {
        let districtDoc = firestore.doc("districts/" + district.id);
        subdistricts = subdistricts.where("districtRef", "==", districtDoc);
      }

      let language = getLanguageFromLocale($locale);
      let sortField = "name";
      if (language === "th") {
        sortField = "name_th";
      }

      return subdistricts
        .orderBy(sortField)
        .get()
        .then(function(snapshot) {
          let results = [];
          snapshot.forEach(function(doc) {
            let name = lget(doc, "name", $locale);
            if (name.toLowerCase().indexOf(query.toLowerCase()) >= 0) {
              results.push({
                doc: doc,
                name: name
              });
            }
          });
          return results;
        })
        .catch(function(e) {
          console.log(e);
        });
    });
  }

  async function save() {
    if ($dirty) {
      let dialog = f7.dialog.preloader($_("address.dialog.savingChanges"));
      return addressDoc
        .update({
          building: $building,
          moo: $moo,
          street: $street,
          subdistrict: $subdistrict,
          district: $district,
          province: $province,
          zipcode: $zipcode,
          country: $country,
          deliverable: $deliverable,
          location:
            location != null
              ? new firebase.firestore.GeoPoint(location.lat, location.lng)
              : null,
          mismatch: mismatch
        })
        .then(function() {
          dialog.close();
          dirty.set(false);
          return true;
        });
    } else {
      return false;
    }
  }

  function create() {
    return getProfile().then(function(profile) {
      let dialog = f7.dialog.preloader($_("address.dialog.savingNewAddress"));

      return getSecuredFirestore().then(function(firestore) {
        let addressesRef = firestore.collection(
          "users/" + userId + "/addresses"
        );

        return getCachedFirst(addressesRef).then(function(snapshot) {
          let primary = snapshot.size === 0;
          let doc = addressesRef.doc();
          return doc
            .set({
              building: $building,
              moo: $moo,
              street: $street,
              subdistrict: $subdistrict,
              district: $district,
              province: $province,
              zipcode: $zipcode,
              country: $country,
              deliverable: $deliverable,
              location: location
                ? new firebase.firestore.GeoPoint(location.lat, location.lng)
                : null,
              mismatch: mismatch,
              primary: primary
            })
            .then(function() {
              dialog.close();
              addressDoc = doc;
              addressId = doc.id;
              dirty.set(false);
              history.replaceState({}, document.title, "/Address/" + doc.id);
            });
        });
      });
    });
  }

  function removeLocation() {
    f7.dialog.confirm(
      $_("address.dialog.removeLocation.description"),
      $_("address.dialog.removeLocation.title"),
      () => {
        deliveryLocation.set(null);
      }
    );
  }

  function syncLocation() {
    let address = {
      building: $building,
      moo: $moo,
      street: $street,
      subdistrict: $subdistrict,
      district: $district,
      province: $province,
      country: $country,
      zipcode: $zipcode,
      mode: COMPACT
    };

    geocode(formatAddress(address)).then(function(locations) {
      let location = locations[0];
      deliveryLocation.set(location);

      isDeliverable(location).then(function(result) {
        deliverable.set(result);
      });
      mismatch = false;
    });
  }

  function deleteDeliveryAddress() {
    getProfile().then(function(profile) {
      deletableAddress(userId, addressDoc.id).then(function(deletable) {
        if (deletable) {
          f7.dialog.confirm(
            $_("address.dialog.deleteAddress.description"),
            () => {
              let dialog = f7.dialog.preloader(
                $_("address.dialog.deletingAddress")
              );

              deleteAddress(userId, addressDoc.id, primary)
                .then(function() {
                  dialog.close();
                  f7router.back();
                })
                .catch(function(e) {
                  console.log(e);
                  f7.dialog.alert(
                    $_("address.dialog.deleteAddress.error"),
                    $_("address.dialog.deleteAddress.title")
                  );
                });
            }
          );
        } else {
          f7.dialog
            .create({
              title: $_("address.dialog.deleteAddress.title"),
              text: $_("address.dialog.deleteAddress.referenced"),
              buttons: [
                {
                  text: $_("base.cancel")
                },
                {
                  text: $_("address.dialog.deleteAddress.button.openOrder"),
                  bold: true,
                  onClick: function() {
                    f7router.navigate("/Order/New");
                  }
                }
              ],
              verticalButtons: false
            })
            .open();
        }
      });
    });
  }

  function openLocation() {
    f7router.navigate("/Location", {
      props: {
        building: building,
        moo: moo,
        street: street,
        subdistrict: subdistrict,
        district: district,
        province: province,
        zipcode: zipcode,
        country: country,
        deliverable: deliverable,
        deliveryLocation: deliveryLocation,
        addressId: addressId
      }
    });
  }
</script>

<style>
  :global(.dialog-button) {
    text-align: center !important;
  }
</style>

<Page on:pageAfterIn={onPageInit}>
  <Navbar>
    <NavLeft>
      <Link
        icon="icon icon-back"
        on:click={function() {
          if ($dirty) {
            showUnsavedChangesDialog(function() {
              f7router.back();
            });
          } else {
            f7router.back();
          }
        }} />
    </NavLeft>
    <div class="title">{navTitle}</div>
    <NavRight>
      <Link
        searchbarEnable=".page-current .searchbar"
        icon="icon icon-search" />
      {#if addressId}
        <Link
          style="color: rgb(0, 122, 255)"
          icon="icon icon-save"
          class={$dirty && valid ? '' : 'disabled'}
          on:click={function() {
            save()
              .then(function(saved) {
                if (saved) {
                  f7.toast
                    .create({
                      text: $_('address.save.successful'),
                      position: 'bottom',
                      closeTimeout: 4000
                    })
                    .open();
                } else {
                  f7.toast
                    .create({
                      text: $_('address.save.noChanges'),
                      position: 'bottom',
                      closeTimeout: 4000
                    })
                    .open();
                }
              })
              .catch(function(e) {
                console.log(e);
                f7.dialog.alert($_('address.save.error'), $_('address.dialog.button.saveChanges'));
              });
          }} />
      {:else}
        <Link
          style="color: rgb(0, 122, 255)"
          icon="icon icon-save"
          class={$dirty && valid ? '' : 'disabled'}
          on:click={function() {
            create()
              .then(function() {
                f7.toast
                  .create({
                    text: $_('address.new.successful'),
                    position: 'bottom',
                    closeTimeout: 4000
                  })
                  .open();
              })
              .catch(function(e) {
                console.log(e);
                f7.dialog.alert($_('address.new.error'), $_('address.dialog.button.saveNewAddress'));
              });
          }} />
      {/if}
    </NavRight>
    <Searchbar
      class="searchbar"
      placeholder={$_('address.searchbar.placeholder')}
      id="searchbar-autocomplete"
      init={false}
      expandable={true} />
  </Navbar>

  <div style="margin-top: 0px; margin-bottom: 50px; padding-top: 10px">
    <BlockTitle medium style="display: flex; justify-content: space-between;">
      <div>{primary ? $_('address.primaryTitle') : $_('address.title')}</div>
    </BlockTitle>
    <List
      form
      style="margin-bottom: 5px"
      inlineLabels
      noHairlines
      accordionList>
      <ListItem
        accordionItem
        accordionItemOpened={$building || $moo}
        title={$_('address.buildingVillage')}>
        <AccordionContent>
          <List>
            <ListInput
              label={$_('address.buildingFloor')}
              type="text"
              value={$building}
              onChange={e => updateBuilding(e.target.value, true)}
              placeholder={$_('address.buildingFloor.placeholder')}
              clearButton />
            <ListInput
              label="Moo"
              type="text"
              value={$moo}
              onChange={e => updateMoo(e.target.value, true)}
              placeholder={$_('address.village.placeholder')}
              clearButton />
          </List>
        </AccordionContent>
      </ListItem>

      <ListInput
        label={$_('address.street')}
        type="text"
        value={$street}
        onChange={e => updateStreet(e.target.value, true)}
        placeholder={$_('address.street.placeholder')}
        required
        validate
        clearButton />

      <ListInput
        inputId="subdistrict"
        label={$_('address.subdistrict')}
        type="text"
        value={$subdistrict}
        onChange={e => updateSubdistrict(e.target.value, true)}
        placeholder={$_('address.subdistrict.placeholder')}
        required
        validate
        autocomplete
        clearButton />

      <ListInput
        inputId="district"
        label={$_('address.district')}
        type="text"
        value={$district}
        onChange={e => updateDistrict(e.target.value, true)}
        placeholder={$_('address.district.placeholder')}
        required
        validate
        autocomplete
        clearButton />

      <ListInput
        inputId="province"
        label={$_('address.province')}
        type="text"
        value={$province}
        onChange={e => updateProvince(e.target.value, true)}
        placeholder={$_('address.province.placeholder')}
        required
        validate
        autocomplete
        clearButton />

      <ListInput
        inputId="zipcode"
        label={$_('address.zipcode')}
        type="text"
        value={$zipcode}
        onChange={e => updateZipcode(e.target.value, true)}
        placeholder={$_('address.zipcode.placeholder')}
        required
        validate
        autocomplete
        clearButton />

      <ListInput
        label={$_('address.country')}
        type="text"
        value={$country}
        required
        validate
        readonly
        disabled />

      {#if location != null && mismatch && valid}
        <ListItem style="background-color: #ff00000a">
          <div style="width: 100%; padding-top: 20px;" class="item-title">
            <span style="color: red; font-weight: bold">
              {$_('address.locationMismatch.title')}
            </span>
            <div class="item-text">
              {$_('address.locationMismatch.description')}
            </div>
            <Button
              style="margin: 20px; margin-left: 5px; margin-right: 5px"
              color="red"
              small
              fill
              round
              on:click={syncLocation}>
              {$_('address.locationMismatch.sync')}
              <Icon
                style="margin-left: 4px; vertical-align: text-top"
                icon="icon-location" />
            </Button>
          </div>
          <div class="item-after">
            <Icon
              size="24px"
              style="color: red"
              icon="icon icon-undeliverable" />
          </div>
        </ListItem>
      {/if}

      {#if location != null}
        {#if $deliverable}
          <ListItem style="background-color: #00800036">
            <div style="width: 100%; padding-top: 20px;" class="item-title">
              <span style="color: green; font-weight: bold">
                {$_('address.deliverable.title')}
              </span>
              <div class="item-text">
                {$_('address.deliverable.description')}
              </div>
            </div>
            <div class="item-after">
              <Icon
                size="24px"
                style="color: green"
                icon="icon icon-deliverable" />
            </div>
          </ListItem>
        {:else}
          <ListItem
            style="background-color: #ff00000a; padding-top: 20px;
            padding-bottom: 20px">
            <div style="width: 100%" class="item-title">
              <span style="color: red; font-weight: bold">
                {$_('address.undeliverable.title')}
              </span>
              <div class="item-text">
                {$_('address.undeliverable.description')}
              </div>
              <Button
                style="margin: 20px; margin-left: 5px; margin-right: 5px"
                color="red"
                small
                fill
                round
                on:click={function() {
                  let message = formatAddress({
                    building: $building,
                    moo: $moo,
                    street: $street,
                    subdistrict: $subdistrict,
                    district: $district,
                    province: $province,
                    country: $country,
                    zipcode: $zipcode,
                    mode: NEW_LINE
                  });
                  if ($deliveryLocation) {
                    message += '\n' + 'https://www.google.com/maps/search/' + $deliveryLocation.lat + ',' + $deliveryLocation.lng;
                  }
                  sendDeliveryRequest(message);
                }}>
                {$_('address.button.deliveryRequest')}
                <Icon
                  style="margin-left: 4px; vertical-align: text-top"
                  icon="icon-line" />
              </Button>
            </div>
            <div class="item-after">
              <Icon
                size="24px"
                style="color: red"
                icon="icon icon-undeliverable" />
            </div>
          </ListItem>
        {/if}
      {:else if deliverable != null && !$deliverable}
        <ListItem style="padding-top: 20px; padding-bottom: 20px">
          <div style="width: 100%;" class="item-title">
            <span style="color: orange; font-weight: bold">
              {$_('address.deliverability.title')}
            </span>
            <div class="item-text">
              {$_('address.deliverability.description')}
            </div>
            <Button
              style="margin: 20px; margin-left: 5px; margin-right: 5px"
              color="orange"
              small
              fill
              round
              on:click={openLocation}>
              {$_('address.setLocation')}
              <Icon
                style="margin-left: 4px; vertical-align: text-top"
                icon="icon-location" />
            </Button>
          </div>
        </ListItem>
      {/if}
    </List>

    <AddressCanvas {address} />

    <BlockTitle medium>{$_('address.location.title')}</BlockTitle>
    {#if locationImage}
      <Link on:click={openLocation}>
        <img
          style="width: 100%; margin-left: 10px; margin-right: 10px;
          margin-bottom: 20px; border-radius: 5px"
          alt="Delivery Location"
          src={locationImage} />
      </Link>
    {:else}
      <div style="position: relative; margin-left: 10px; margin-right: 10px;">
        <img
          style="opacity: 0.5; width: 100%; border-radius: 5px; position:
          relative;"
          alt="Delivery Location"
          src="/staticmap.png" />
        <Button
          on:click={openLocation}
          fill
          round
          color="orange"
          icon="icon icon-location"
          style="position: absolute; top: 50%; left: 50%; transform:
          translateX(-50%) translateY(-50%);">
          {$_('address.toolbar.setLocation')}
        </Button>
      </div>
    {/if}
  </div>

  <Toolbar
    style="font-size: 0.6rem; line-height: 1.4rem"
    labels
    position={'bottom'}>

    <Link
      class={primaryClass}
      text={$_('address.toolbar.makePrimary')}
      style="color: rgb(0, 122, 255)"
      icon="icon icon-primary"
      on:click={function() {
        setPrimaryAddress(userId, addressId).then(function() {
          primary = true;
        });
      }} />

    {#if $deliveryLocation}
      <Link
        text={$_('address.toolbar.editLocation')}
        style="color: rgb(0, 122, 255)"
        icon="icon icon-location"
        on:click={openLocation} />
      <Link
        text={$_('address.toolbar.removeLocation')}
        style="color: red"
        icon="icon icon-close"
        on:click={removeLocation} />
    {:else}
      <Link
        text={$_('address.toolbar.setLocation')}
        style="color: rgb(0, 122, 255)"
        icon="icon icon-location"
        on:click={openLocation} />
    {/if}

    {#if addressId}
      <Link
        text={$_('address.toolbar.deleteAddress')}
        style="color: red"
        icon="icon icon-bin"
        on:click={deleteDeliveryAddress} />
    {/if}
  </Toolbar>
</Page>
